import React from 'react';
import type { NodeTypeInfo, WorkflowDef } from '../types';

interface NodeData {
  id: string;
  type: string;
  label: string;
  prompt: string;
  config: Record<string, any>;
  position: { x: number; y: number };
}

interface NodeEditorProps {
  node: NodeData;
  nodeTypeInfo: NodeTypeInfo | null;
  workflow?: WorkflowDef | null;
  onChange: (updated: NodeData) => void;
  onWorkflowChange?: (wf: WorkflowDef) => void;
  onClose: () => void;
}

const normalizeRouteKey = (value: string) =>
  String(value || '').trim().toLowerCase().replace(/\s+/g, '_');

const aggregatorPrompt =
  'Combine the selected branch outputs into one coherent response. Preserve important details and resolve contradictions.\n\nBranch outputs:\n{responses}';

const clampFollowUpValue = (value: string, fallback: number) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, Math.min(10, parsed));
};

export default function NodeEditor({
  node,
  nodeTypeInfo,
  workflow = null,
  onChange,
  onWorkflowChange,
  onClose,
}: NodeEditorProps) {
  const outgoingEdges = React.useMemo(
    () => (workflow?.edges || []).filter(edge => edge.source === node.id),
    [workflow, node.id],
  );
  const nodeById = React.useMemo(() => {
    const map: Record<string, WorkflowDef['nodes'][number]> = {};
    for (const candidate of workflow?.nodes || []) {
      map[candidate.id] = candidate;
    }
    return map;
  }, [workflow]);
  const routeRows = outgoingEdges.map(edge => {
    const target = nodeById[edge.target];
    const key = normalizeRouteKey(edge.condition || target?.config?.domain || edge.target);
    return { edge, target, key };
  });
  const routerMode = String(node.config.router_mode || 'single');
  const routeModes = typeof node.config.route_modes === 'object' && node.config.route_modes
    ? node.config.route_modes
    : {};
  const defaultRouteOptions = routeRows.filter(
    row => routerMode !== 'hybrid' || (routeModes[row.key] || 'parallel') === 'exclusive',
  );
  const configuredDefaultRoute = normalizeRouteKey(node.config.default_route || '');
  const selectedDefaultRoute = defaultRouteOptions.some(row => row.key === configuredDefaultRoute)
    ? configuredDefaultRoute
    : '';
  const aggregatorTargets = routeRows.map(row => {
    const targetOutgoing = (workflow?.edges || []).filter(edge => edge.source === row.edge.target);
    return targetOutgoing
      .map(edge => nodeById[edge.target])
      .find(target => target?.type === 'aggregator');
  });
  const commonAggregatorId = aggregatorTargets[0]?.id || '';
  const hasCommonAggregator = routeRows.length > 0 && aggregatorTargets.every(
    target => target && target.id === commonAggregatorId,
  );
  const needsAggregator = node.type === 'router' && routerMode !== 'single';

  const updateRouterMode = (mode: string) => {
    const nextConfig: Record<string, any> = { ...node.config, router_mode: mode };
    if (mode === 'single') {
      delete nextConfig.route_modes;
    } else if (mode === 'multi') {
      nextConfig.route_modes = Object.fromEntries(routeRows.map(row => [row.key, 'parallel']));
    } else if (mode === 'hybrid') {
      nextConfig.route_modes = Object.fromEntries(
        routeRows.map((row, index) => [row.key, routeModes[row.key] || (index === 0 ? 'exclusive' : 'parallel')]),
      );
      const nextExclusiveRoutes = routeRows.filter(
        (row, index) => (nextConfig.route_modes[row.key] || (index === 0 ? 'exclusive' : 'parallel')) === 'exclusive',
      );
      if (!nextExclusiveRoutes.some(row => row.key === normalizeRouteKey(nextConfig.default_route || ''))) {
        nextConfig.default_route = nextExclusiveRoutes[0]?.key || '';
      }
    }
    onChange({ ...node, config: nextConfig });
  };

  const updateRouteKind = (routeKey: string, kind: string) => {
    const nextRouteModes = {
      ...routeModes,
      [routeKey]: kind,
    };
    const nextConfig: Record<string, any> = {
      ...node.config,
      route_modes: nextRouteModes,
    };
    if (
      routerMode === 'hybrid' &&
      routeKey === configuredDefaultRoute &&
      kind !== 'exclusive'
    ) {
      nextConfig.default_route = routeRows.find(
        row => row.key !== routeKey && (nextRouteModes[row.key] || 'parallel') === 'exclusive',
      )?.key || '';
    }
    onChange({
      ...node,
      config: nextConfig,
    });
  };

  const addOrConnectAggregator = () => {
    if (!workflow || !onWorkflowChange || routeRows.length === 0) return;
    const existingAggregator = aggregatorTargets.find(Boolean);
    const aggregator = existingAggregator || {
      id: `aggregator_${Date.now()}`,
      type: 'aggregator',
      label: 'Aggregator',
      prompt: aggregatorPrompt,
      config: {},
      position: {
        x: node.position.x + 420,
        y: node.position.y + 160,
      },
    };

    const nextNodes = existingAggregator ? workflow.nodes : [...workflow.nodes, aggregator];
    const edgeIds = new Set(workflow.edges.map(edge => edge.id));
    const edgePairs = new Set(workflow.edges.map(edge => `${edge.source}->${edge.target}`));
    const nextEdges = [...workflow.edges];

    for (const row of routeRows) {
      const pair = `${row.edge.target}->${aggregator.id}`;
      if (edgePairs.has(pair)) continue;
      let id = `e-${row.edge.target}-${aggregator.id}`;
      if (edgeIds.has(id)) id = `${id}-${Date.now()}`;
      edgeIds.add(id);
      edgePairs.add(pair);
      nextEdges.push({
        id,
        source: row.edge.target,
        target: aggregator.id,
      });
    }

    onWorkflowChange({ ...workflow, nodes: nextNodes, edges: nextEdges });
  };

  return (
    <div className="node-editor">
      <h3>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: nodeTypeInfo?.color || '#6366f1',
            }}
          />
          Edit Node
        </span>
        <button className="close-btn" type="button" onClick={onClose}>x</button>
      </h3>

      <label>Label</label>
      <input
        value={node.label}
        onChange={e => onChange({ ...node, label: e.target.value })}
      />

      <label>Type</label>
      <input value={node.type} disabled style={{ opacity: 0.6 }} />

      <label>System Prompt</label>
      <textarea
        value={node.prompt}
        onChange={e => onChange({ ...node, prompt: e.target.value })}
        placeholder="Enter the system prompt for this node..."
      />

      {node.type === 'domain_agent' && (
        <>
          <label>Domain Name *</label>
          <input
            value={node.config.domain || ''}
            onChange={e =>
              onChange({ ...node, config: { ...node.config, domain: e.target.value } })
            }
            placeholder="e.g. cardiology, neurology"
            required
          />
          <div className="router-note">
            Choose a clear route name. Router nodes use this value to decide the flow.
          </div>

          <label>Route Description</label>
          <input
            value={node.config.route_description || ''}
            onChange={e =>
              onChange({ ...node, config: { ...node.config, route_description: e.target.value } })
            }
            placeholder="One line to help the router understand this agent"
          />
        </>
      )}

      {node.type === 'intake_agent' && (
        <div className="intake-settings">
          <label>Follow-Up Depth</label>
          <select
            value={node.config.follow_up_depth || 'medium'}
            onChange={e =>
              onChange({ ...node, config: { ...node.config, follow_up_depth: e.target.value } })
            }
          >
            <option value="minimal">Minimal</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          <div className="router-note">
            Controls how aggressively the intake agent asks for missing information before continuing.
          </div>

          <label>Minimum Follow-Ups</label>
          <input
            type="number"
            min={0}
            max={10}
            value={node.config.follow_up_min ?? ''}
            placeholder={node.config.follow_up_depth === 'high' ? '1' : '0'}
            onChange={e => {
              const nextMin = e.target.value === '' ? undefined : clampFollowUpValue(e.target.value, 0);
              const nextConfig = { ...node.config, follow_up_min: nextMin };
              if (nextMin === undefined) delete nextConfig.follow_up_min;
              onChange({ ...node, config: nextConfig });
            }}
          />

          <label>Maximum Follow-Ups</label>
          <input
            type="number"
            min={0}
            max={10}
            value={node.config.follow_up_max ?? ''}
            placeholder={
              node.config.follow_up_depth === 'minimal'
                ? '1'
                : node.config.follow_up_depth === 'high'
                  ? '4'
                  : '2'
            }
            onChange={e => {
              const nextMax = e.target.value === '' ? undefined : clampFollowUpValue(e.target.value, 2);
              const nextConfig = { ...node.config, follow_up_max: nextMax };
              if (nextMax === undefined) delete nextConfig.follow_up_max;
              onChange({ ...node, config: nextConfig });
            }}
          />
          <div className="router-note">
            The maximum is enforced at runtime, so intake cannot loop indefinitely.
          </div>
        </div>
      )}

      {node.type === 'router' && (
        <div className="router-settings">
          <label>Router Mode</label>
          <select
            value={routerMode}
            onChange={e => updateRouterMode(e.target.value)}
          >
            <option value="single">Single route</option>
            <option value="multi">Multi route / parallel</option>
            <option value="hybrid">Hybrid</option>
          </select>

          <label>Default Route</label>
          <select
            value={selectedDefaultRoute}
            onChange={e =>
              onChange({ ...node, config: { ...node.config, default_route: e.target.value } })
            }
            disabled={defaultRouteOptions.length === 0}
          >
            <option value="">First available route</option>
            {defaultRouteOptions.map(row => (
              <option key={row.edge.id} value={row.key}>
                {row.target?.label ? `${row.target.label} (${row.key})` : row.key}
              </option>
            ))}
          </select>
          <div className="router-note">
            Used only when no valid route can be identified. Hybrid routers can default to an exclusive route.
          </div>

          {routerMode === 'hybrid' && routeRows.length > 0 && (
            <div className="route-mode-list">
              {routeRows.map(row => (
                <div key={row.edge.id} className="route-mode-row">
                  <span>{row.key}</span>
                  <select
                    value={routeModes[row.key] || 'parallel'}
                    onChange={e => updateRouteKind(row.key, e.target.value)}
                  >
                    <option value="exclusive">Exclusive</option>
                    <option value="parallel">Parallel</option>
                  </select>
                </div>
              ))}
            </div>
          )}

          {routerMode === 'multi' && routeRows.length > 0 && (
            <div className="router-note">
              All outgoing routes are parallel selectable in multi-route mode.
            </div>
          )}

          {needsAggregator && (
            <div className={`router-warning ${hasCommonAggregator ? 'ok' : ''}`}>
              {hasCommonAggregator
                ? `Branches converge into ${aggregatorTargets[0]?.label || 'Aggregator'}.`
                : 'All router branches must connect to one Aggregator before the workflow can run.'}
              <button className="btn-secondary" type="button" onClick={addOrConnectAggregator} disabled={!workflow}>
                {hasCommonAggregator ? 'Reconnect Aggregator' : 'Add / Connect Aggregator'}
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '12px', fontSize: '11px', color: '#64748b' }}>
        <strong>Variables:</strong> Use {'{query}'}, {'{conversation}'}, {'{follow_up_depth}'}, {'{domain}'}, {'{response}'}, {'{responses}'} in prompts.
        They get replaced at runtime.
      </div>
    </div>
  );
}
